import React, { useState, useEffect } from 'react';
import { FixedSizeList as List } from 'react-window';
import { useTranslation } from 'react-i18next';
import { Button, makeStyles } from '@material-ui/core';
import { Add } from '@material-ui/icons';
import FilterButton from '../../Button/FilterButton';
import Search from '../../Search';
import SearchableChecklistItem, { ISearchableChecklistItem } from './SearchableChecklistItem';
import useSearchableChecklist, { IFiltersInterface } from './useSearchableChecklist';
import ListError from './ListError';
import ListLoading from './ListLoading';
import ListNoData from './ListNoData';
import Filter from './Filter';
import { FilterOptionsType } from '../AdvancedChecklist/state/AdvancedChecklist.actions';

export interface IFilter {
    key: string;
    expectedValue: any;
}

export interface IFilterOption {
    key: string;
    title: string;
    filterKey: string;
    itemKey: string;
    items: ISearchableChecklistItem[];
}

export interface IFilterOptions {
    [key: string]: IFilterOption;
}
export interface ISearchableChecklist {
    description?: string | JSX.Element;
    subtitle?: (number: number) => string | JSX.Element;
    items: ISearchableChecklistItem[];
    searchName?: string;
    submitText?: string;
    onSubmit: (selected: ISearchableChecklistItem[], changed: ISearchableChecklistItem[]) => void;
    onCreate?: (selected: ISearchableChecklistItem[], changed: ISearchableChecklistItem[]) => void;
    loading?: boolean;
    loadingMessage?: React.ReactNode;
    errorMessage?: React.ReactNode;
    noDataMessage?: React.ReactNode;
    error?: boolean;
    hasFrame?: boolean;
    hasButtons?: boolean;
    hasSearch?: boolean;
    hasFilters?: boolean;
    hasCreateButton?: boolean;
    isZeroAcceptable?: boolean;
    extraFooterButtons?: JSX.Element;
    itemToolbar?: (id: string, getSelected: () => ISearchableChecklistItem[]) => JSX.Element;
    filterOptions?: FilterOptionsType[];
    createButtonText?: string;
    selectedFilterCount?: number;
    isVirtualizeList?: boolean;
    placeholderOverride?: string;
    widthAuto?: boolean;
    hasFooter?: boolean;
    onChange?: (selected: ISearchableChecklistItem[]) => void;
    maxHeight?: string;
    bodyOverflow?: string;
    resetSelected?: boolean;
    InfoCard?: React.ReactNode;
}

const SearchableChecklist = (props: ISearchableChecklist) => {
    const [items, setitems] = useState<ISearchableChecklistItem[]>([]);

    useEffect(() => {
        if (props.items.length !== items.length) {
            setitems(props.items);
        }
        // eslint-disable-next-line
    }, [props.items]);

    useEffect(() => {
        selectNone();
        setResetSelectedFilters(!resetSelectedFilters);
        // eslint-disable-next-line
    }, [props.resetSelected]);
    const useStyles = makeStyles({
        root: {
            border: props.hasFrame ? '1px solid #E8EAF4' : undefined,
            paddingBottom: props.hasFrame ? '5.5rem' : 0,
            borderRadius: '16px',
            overflow: 'hidden',
            maxHeight: props.maxHeight ?? '70vh',
            minHeight: '40px',
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '720px',
            backgroundColor: '#FFFFFF',
            position: 'relative',
            left: '50%',
            transform: 'translate(-50%)',
            width: props.widthAuto ? 'auto' : '25rem',
            '@media only screen and (min-width: 767px)': {
                width: props.widthAuto ? 'auto' : '49.75rem',
            },
        },
        header: {
            padding: props.hasFrame ? '0 1.25rem 2.5rem 1.25rem' : '0 1.25rem 1rem 1.25rem',
            borderBottom: '1px solid #E8EAF4',
        },
        body: {
            padding: props.hasFrame ? '1.25rem 2.5rem' : '1.25rem 1rem',
            overflow: props.bodyOverflow ?? props.hasFrame ? 'auto' : 'hidden',
            minHeight: '30vh',
        },
        footer: {
            textAlign: 'right',
            padding: props.hasFrame ? '1.25rem 2.5rem' : '1.25rem 3.75rem',
            position: 'absolute',
            bottom: 0,
            width: '100%',
            height: '5.5rem',
            borderRadius: props.hasFrame ? '0 0 16px 16px' : undefined,
            left: 0,
            backgroundColor: props.hasFrame ? '#F3F4F9' : undefined,
            '& button:last-child': {
                padding: props.hasFrame ? '8px 22px' : '0.875rem 1.5625rem!important',
            },
        },
        description: {
            fontSize: '1.1rem',
            fontWeight: 400,
            color: '#616161',
            margin: 0,
        },
        counter: {
            float: 'left',
            fontWeight: 600,
            fontSize: '0.9rem',
            color: '#212121',
        },
        selections: {
            boxSizing: 'border-box',
            padding: props.hasFrame ? '0.5rem 0' : '0 0 0.9375rem 0',
            width: '100%',
            display: 'grid',
            alignItems: 'center',
            justifyContent: 'space-between',
            gridTemplateColumns: '60% 40%',
        },
        selectButtonWrapper: {
            float: 'right',
            display: 'flex',
            justifyContent: 'flex-end',
        },
        selectButton: {
            margin: '0 3px',
            padding: '0.3125rem 0.625rem',
            borderRadius: '3.5rem',
            textTransform: 'capitalize',
            backgroundColor: '#F3F4F9 !important',
            '& p': {
                fontStyle: 'normal',
                fontWeight: 'normal',
                fontSize: '0.8125rem',
                lineHeight: '1.125rem',
                display: 'flex',
                alignItems: 'center',
                textAlign: 'center',
                color: '#212121',
                margin: 0,
            },
            '&:hover': {
                backgroundColor: 'initial !improtant',
            },
        },
        actionBar: {
            display: 'flex',
            flexWrap: 'wrap',
            '@media only screen and (min-width: 767px)': {
                flexWrap: 'nowrap',
            },
            '& div:first-child': {
                borderRadius: '8px!important',
                width: '100%',
            },
            '& div:last-child': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                flexDirection: 'row',
                '@media only screen and (max-width: 767px)': {
                    marginTop: '0.5rem',
                },
                '& button': {
                    padding: '0.6875rem 0.9375rem!important',
                    borderRadius: '8px!important',
                    background: '#FFFFFF',
                    border: '1px solid #C1CAE1',
                    '@media only screen and (min-width: 767px)': {
                        marginLeft: '0.9375rem',
                    },
                    '@media only screen and (max-width: 767px)': {
                        marginRight: '0.9375rem',
                    },
                    '& span:first-child': {
                        '& p': {
                            fontStyle: 'normal',
                            fontWeight: 'normal',
                            fontSize: '0.875rem!important',
                            lineHeight: '1.1875rem!important',
                            display: 'flex',
                            alignItems: 'center',
                            textAlign: 'center',
                            color: '#212121',
                            margin: 0,
                        },
                    },
                },
            },
        },
        filters: {},
        createButton: {
            minWidth: '184px',
        },
    });

    const classes = useStyles();
    const { t } = useTranslation();
    const {
        currentList,
        setCurrentList,
        getSelected,
        getChanged,
        selectNone,
        selectAll,
        onSearchChange,
        onCheckboxChange,
        selectedCount,
        applyFilters,
        filteredList,
    } = useSearchableChecklist(items);

    useEffect(() => {
        if (props.onChange) {
            props.onChange(getSelected());
        }
        // eslint-disable-next-line
    }, [selectedCount]);

    const [filters, setFilters] = useState<IFiltersInterface>({});
    const [filterOptions, setFilterOptions] = useState<{ [key: string]: any }>({});
    const [filtersOn, setFiltersOn] = useState<boolean>(false);
    const [resetSelectedFilters, setResetSelectedFilters] = useState<boolean>(false);
    const [counters, setCounters] = useState<{ [key: string]: number }>({});
    const [totalCount, setTotalCount] = useState<number>(0);
    const [searchValue, setSearchValue] = useState<string>('');

    const onSubmit = () => {
        props.onSubmit(getSelected(), getChanged());
    };

    useEffect(() => {
        if (props.filterOptions) {
            setFilterOptions(props.filterOptions);
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        applyFilters(filters, searchValue);
        // eslint-disable-next-line
    }, [filters]);

    useEffect(() => {
        if (Object.keys(filters).length > 0 && filteredList === null) {
            applyFilters(filters, searchValue);
        }
        // eslint-disable-next-line
    }, [currentList]);

    useEffect(() => {
        if (props.error || props.loading) {
            selectNone();
        }
        //eslint-disable-next-line
    }, [props.error, props.loading]);

    useEffect(() => {
        onSearchChange(searchValue);
        //eslint-disable-next-line
    }, [searchValue]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const totalCount = Object.values(counters).reduce((total: number, current: number) => {
            return total + current;
        }, 0);

        setTotalCount(totalCount);

        //eslint-disable-next-line
    }, [counters]);

    useEffect(() => {
        setCurrentList(items);
    }, [items, setCurrentList]);

    function Row({ index, style }: any) {
        // searchValue.length > 0 ? filteredList[index] :
        const item: ISearchableChecklistItem =
            filteredList !== null && filteredList !== undefined ? filteredList[index] : currentList[index];
        return (
            <div style={style} key={index} data-component-name={item.name_public}>
                <SearchableChecklistItem
                    {...item}
                    content={item.content ?? item.name_public}
                    index={index}
                    onCheckboxChange={onCheckboxChange}
                    hasFrame={props.hasFrame}
                    itemToolbar={props.itemToolbar}
                    getSelected={getSelected}
                />
            </div>
        );
    }

    const renderNoItems = () => {
        return <div>{t('errors.searchableChecklist.notFound')}</div>;
    };

    const renderItems = () => {
        const itemCount =
            filteredList !== null && filteredList !== undefined ? filteredList?.length : currentList.length;
        if (itemCount === 0) return renderNoItems();
        if (props.error) return props.errorMessage || <ListError message={props.errorMessage} />;
        if (props.loading) return props.loadingMessage || <ListLoading />;
        if (currentList.length < 1) return props.noDataMessage || <ListNoData message={props.noDataMessage} />;
        if (props.isVirtualizeList) {
            return (
                <List
                    height={window.innerHeight > 1052 ? 500 : 260}
                    itemCount={itemCount}
                    itemSize={window.innerWidth > 767 ? 76 : 136}
                    width={'100%'}
                    style={{
                        paddingBottom: '5rem',
                        overflow: 'auto',
                    }}
                    useIsScrolling
                    //this is selector for cypress tests
                    className={'SEARCHABLE_CHECK_LIST'}
                >
                    {Row}
                </List>
            );
        }
        if (filteredList !== null && filteredList !== undefined) {
            return filteredList.map((item: ISearchableChecklistItem, i: number) => (
                <SearchableChecklistItem
                    {...item}
                    content={item.content ?? item.name_public}
                    index={i}
                    onCheckboxChange={onCheckboxChange}
                    hasFrame={props.hasFrame}
                    itemToolbar={props.itemToolbar}
                />
            ));
        } else {
            return currentList.map((item: ISearchableChecklistItem, i: number) => (
                <SearchableChecklistItem
                    {...item}
                    content={item.content ?? item.name_public}
                    index={i}
                    onCheckboxChange={onCheckboxChange}
                    hasFrame={props.hasFrame}
                    itemToolbar={props.itemToolbar}
                />
            ));
        }
    };

    const onCreate = () => {
        if (props.onCreate) {
            props.onCreate(getSelected(), getChanged());
        }
    };
    const onClearFilters = (filter: IFilterOption) => {
        const countersCloned = { ...counters };

        countersCloned[filter.key] = 0;

        setCounters(countersCloned);

        // setCurrentList(props.items);

        setFilters({});
    };
    return (
        <>
            <div className={classes.root}>
                <div className={classes.header}>
                    <div className={classes.selections}>
                        <div>
                            {props.description && <h4 className={classes.description}>{props.description}</h4>}
                            {props.submitText && (
                                <div className={classes.counter} data-component-name="SELECTED_ITEMS_COUNT">
                                    {props.subtitle
                                        ? props.subtitle(selectedCount)
                                        : t(`misc.itemsSelected`, { selectedCount })}
                                </div>
                            )}
                        </div>
                        {props.hasButtons && (
                            <div className={classes.selectButtonWrapper}>
                                <Button
                                    className={classes.selectButton}
                                    onClick={selectAll}
                                    disabled={
                                        getSelected().length === currentList.length || props.error || props.loading
                                    }
                                    data-component-name="SELECT_ALL_BUTTON"
                                >
                                    <p>{t('misc.selectAll')}</p>
                                </Button>
                                <Button
                                    className={classes.selectButton}
                                    onClick={selectNone}
                                    disabled={getSelected().length === 0 || props.error || props.loading}
                                    data-component-name="SELECT_NONE_BUTTON"
                                >
                                    <p>{t('misc.selectNone')}</p>
                                </Button>
                            </div>
                        )}
                    </div>
                    <div className={classes.actionBar}>
                        {props.hasSearch && (
                            <Search
                                placeholderOverride={t(props.placeholderOverride ?? '')}
                                onChange={(e) => setSearchValue(e.target.value)}
                                searchFields={[{ id: '1', displayName: props.searchName ?? 'Items' }]}
                            />
                        )}
                        <div>
                            {props.hasFilters && (
                                <FilterButton
                                    selectedFilterCount={totalCount}
                                    title={t('misc.filters')}
                                    openFiltersDialog={() => {
                                        setFiltersOn(!filtersOn);
                                    }}
                                />
                            )}
                            {props.hasCreateButton && (
                                <Button
                                    className={classes.createButton}
                                    onClick={onCreate}
                                    data-component-name="CREATE_ITEM_BUTTON"
                                >
                                    <p>
                                        {' '}
                                        <Add /> <span>{props.createButtonText}</span>
                                    </p>
                                </Button>
                            )}
                        </div>
                    </div>
                    {true && (
                        <div
                            className={classes.filters}
                            style={{
                                display: !filtersOn ? 'none' : '',
                            }}
                        >
                            {Object.values(filterOptions || []).map((filter: IFilterOption) => {
                                const onChange = (selecteds: ISearchableChecklistItem[]) => {
                                    // counters[filter.key] = selecteds.length;
                                    setCounters((prev) => {
                                        return {
                                            ...prev,
                                            [filter.key]: selecteds.length,
                                        };
                                    });

                                    const items = selecteds.reduce((total: any, selected: any) => {
                                        return [
                                            ...total,
                                            {
                                                key: filter.itemKey,
                                                expectedValue: selected[filter.filterKey],
                                            },
                                        ];
                                    }, []);
                                    let filtersObject = {
                                        ...filters,
                                        [filter.key]: items,
                                    };

                                    if (selecteds.length === 0) {
                                        delete filtersObject[filter.key];
                                    }
                                    setFilters(filtersObject);
                                };

                                return (
                                    <Filter
                                        items={filter.items}
                                        searchName={filter.title}
                                        onClear={() => onClearFilters(filter)}
                                        onChange={onChange}
                                        resetSelectedFilters={resetSelectedFilters}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className={classes.body}>
                    {renderItems()}
                    {props.InfoCard}
                </div>
            </div>
            {props.hasFooter && (
                <div className={classes.footer}>
                    {props.extraFooterButtons}
                    <Button
                        style={{ marginRight: '1.5625rem', padding: '0.875rem 1.5625rem' }}
                        disabled={selectedCount <= 0 && !props.isZeroAcceptable}
                        variant={'contained'}
                        color={'primary'}
                        onClick={onSubmit}
                        data-component-name="SUBMIT_BUTTON"
                    >
                        {props.submitText}
                    </Button>
                </div>
            )}
        </>
    );
};

SearchableChecklist.defaultProps = {
    hasFrame: true,
    hasDescription: true,
    hasButtons: true,
    hasSearch: true,
    hasFilters: false,
    isZeroAcceptable: false,
    hasCreateButton: false,
    searchName: 'Items',
    submitText: 'Confirm',
    hasFooter: true,
} as Partial<ISearchableChecklist>;

export default SearchableChecklist;
